import type { AnalyticsClient, AnalyticsClientOptions } from "./analyticsClient";
import { ANALYTICS_EVENTS } from "./events";
import type { AnalyticsEventName, AnalyticsEventProperties } from "./events";

/**
 * The one place in the monorepo allowed to touch the Freshpaint SDK.
 *
 * Freshpaint is Breville's ingestion path for all analytics — it routes to
 * Mixpanel, alongside the consumer website, the phone app and Joule devices.
 * It is used here as an explicit event router, never as DOM autocapture.
 *
 * There is no npm package: a snippet injected into the page defines
 * `window.freshpaint` and calls `init()` itself, so this module never
 * initialises anything and no SDK is bundled. The interface below is therefore
 * hand-declared against the methods that snippet installs.
 *
 * This client is deliberately stateless — no buffer, no timer, no lifetime.
 * Pre-load calls are already queued by the snippet itself: it assigns
 * `window.freshpaint` synchronously in an inline script and stubs every method
 * to push into an array until the real library loads. A second buffer here
 * would cover the interval between React running and the global existing,
 * which the inline script guarantees is empty.
 *
 * If Freshpaint is ever loaded asynchronously — via a tag manager, or deferred
 * behind a consent gate — that assumption breaks and buffering becomes real
 * work again. Put it at module scope if so: the global is a singleton and a
 * queue tied to a React component's lifetime cannot survive Strict Mode or a
 * late-resolving `vendorKey`.
 */
interface FreshpaintSdk {
  track: (name: string, properties?: Record<string, unknown>) => void;
  identify: (distinctId: string) => void;
  reset: () => void;
}

// `people.set` and `register` are absent on purpose, not by oversight.
//
// `people.set` writes user *profile* properties, which are current state and
// are overwritten in place — they cannot say what was true when an event
// fired, and they are the one path by which PII could reach a Mixpanel
// profile. Account attribution rides on events instead.
//
// `register` persists super properties to localStorage, so a stale
// `environment` would outlive the build that set it and a stale `role` would
// outlive the session. Root properties are merged per `track` below, from
// values React already holds.

type WindowWithFreshpaint = Window & { freshpaint?: FreshpaintSdk };

/**
 * Resolves the global the snippet installs.
 *
 * No `typeof window` guard: client-shared is browser-only — both consumers are
 * webpack-built SPAs and the Jest environment is jsdom.
 */
export const resolveFreshpaint = (): FreshpaintSdk | undefined => (window as WindowWithFreshpaint).freshpaint;

/** The recursively whitelisted namespace. Everything BCC sends nests under it. */
const PRECISION_EVENT_DATA_KEY = "precisionEventData";
const PRECISION_EVENT_NAMESPACE = "bcc";

export interface FreshpaintAnalyticsClientOptions extends AnalyticsClientOptions {
  /** Injectable for tests; defaults to the global the snippet installs. */
  resolveSdk?: () => FreshpaintSdk | undefined;
}

/**
 * Builds the payload Freshpaint receives.
 *
 * Component properties nest under `precisionEventData.bcc.<component>`, two
 * levels rather than one: the root stays clean, and a flat `bcc.*` bucket
 * would recreate root sprawl one level down. Nesting per component also keeps
 * generic names like `source` and `status` scoped to the component that owns
 * them. Whitelisting is recursive, so one approval on `precisionEventData.bcc`
 * covers everything beneath it.
 */
const buildPayload = (
  subtree: string,
  properties: Record<string, unknown>,
  rootProperties: Record<string, unknown>
): Record<string, unknown> => ({
  ...rootProperties,
  [PRECISION_EVENT_DATA_KEY]: { [PRECISION_EVENT_NAMESPACE]: { [subtree]: properties } },
});

export const createFreshpaintAnalyticsClient = ({
  defaultProperties,
  resolveSdk = resolveFreshpaint,
}: FreshpaintAnalyticsClientOptions = {}): AnalyticsClient => {
  // A throwing SDK — partially initialised, or simply broken — must not take
  // the page down with it. Log without payload values and drop the call: this
  // module exists so analytics cannot break the page it measures.
  const send = (call: (sdk: FreshpaintSdk) => void) => {
    const sdk = resolveSdk();
    if (!sdk) {
      return;
    }

    try {
      call(sdk);
    } catch {
      console.warn("[analytics] transport call failed and was dropped");
    }
  };

  return {
    track: <TName extends AnalyticsEventName>(name: TName, properties: AnalyticsEventProperties<TName>) => {
      const payload = buildPayload(ANALYTICS_EVENTS[name].subtree, properties, { ...defaultProperties });
      send((sdk) => sdk.track(name, payload));
    },
    identify: (distinctId: string) => {
      send((sdk) => sdk.identify(distinctId));
    },
    reset: () => {
      send((sdk) => sdk.reset());
    },
  };
};
